import { useCallback } from 'react';
import useEvent from 'react-use/lib/useEvent';

import {
  Theme,
  Typography,
  WithStyles,
  alpha,
  createStyles,
  withStyles,
} from '@material-ui/core';

import { RiSearchLine } from '@remixicon/react';

import { useDialog } from '@spotify/confidence-core-react';

import AppSearchDialog from './AppSearchDialog';
import { MODIFIER_SYMBOL } from './helpers';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      borderRadius: 999999,

      height: 40,
      width: 345,

      // 1.4 for visual alignment
      padding: theme.spacing(1.5, 1.4),

      display: 'flex',
      alignItems: 'center',

      cursor: 'text',
      color: theme.palette.navigation.color,
      transition: theme.transitions.create('border', {
        duration: theme.transitions.duration.shortest,
      }),
      background: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,

      [theme.breakpoints.down('sm')]: {
        width: 40,
      },

      [theme.breakpoints.down('sm')]: {
        width: 'min-content',
      },
    },
    icon: {
      color: theme.palette.text.primary,
      marginRight: 0,
      [theme.breakpoints.up('md')]: {
        marginRight: theme.spacing(1),
      },
    },
    placeholder: {
      // Hide on mobile
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    hotkey: {
      padding: theme.spacing(0.5, 1),
      marginLeft: 'auto',
      backgroundColor: theme.palette.navigation.background,
      color: theme.palette.navigation.color,

      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${alpha(theme.palette.navigation.color, 0.2)}`,
      whiteSpace: 'nowrap',

      // Hide on mobile
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
  });

export interface AppSearchBarProps extends WithStyles<typeof styles> {}

const AppSearchBar = (props: AppSearchBarProps) => {
  const { classes } = props;
  const { openDialog } = useDialog();

  const handleOpenSearch = useCallback(() => {
    openDialog({
      content: <AppSearchDialog />,
      disableBackdropClick: false,
      dialogProps: {
        maxWidth: 'md',
      },
    });
  }, [openDialog]);

  const onKeyDown = useCallback(
    (data: KeyboardEvent) => {
      if (data.key === 'k' && data.metaKey) {
        handleOpenSearch();
      }
    },
    [handleOpenSearch],
  );

  useEvent('keydown', onKeyDown);

  return (
    <button className={classes.root} onClick={handleOpenSearch}>
      <RiSearchLine size={16} className={classes.icon} />
      <Typography
        component="div"
        className={classes.placeholder}
        color="textSecondary"
        variant="body2"
      >
        Search
      </Typography>
      <div className={classes.hotkey}>{MODIFIER_SYMBOL} K</div>
    </button>
  );
};

export default withStyles(styles, { name: 'AppSearchBar' })(AppSearchBar);
